import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { getEnv } from '../../../lib/env';
import { getFlotiqMainPageUrl, getTestProps } from '../../../lib/helpers';

import {
  CaretRightIcon,
  CodeIcon,
  DiscordMarkIcon,
  DocumentIcon,
  GridIcon,
  TemplateIcon,
} from '../../../images/shapes';

const DashboardQuickLinks = ({ testId = '' }: { testId?: string }) => {
  const { t } = useTranslation();

  const links: {
    key: string;
    title: string;
    description: string;
    label: string;
    link: string;
    icon: ReactNode;
    external?: boolean;
  }[] = [
    {
      key: 'documentation',
      title: t('Global.Documentation'),
      description: t('Home.DocumentationDescription'),
      label: t('Home.ReadOurDocumentation'),
      link: getEnv('VITE_DOCUMENTATION') || '',
      icon: <DocumentIcon className="size-4.5" />,
    },
    {
      key: 'integrations',
      title: t('Home.Integrations'),
      description: t('Home.IntergrationsDescription'),
      label: t('Home.ExploreIntegrations'),
      link: getFlotiqMainPageUrl('integrations'),
      icon: <CodeIcon className="h-5 w-4.5" />,
    },
    {
      key: 'starters',
      title: t('Global.Starters'),
      description: t('Home.StartersDescription'),
      label: t('Home.StartWithTemplate'),
      link: getFlotiqMainPageUrl('starters'),
      icon: <TemplateIcon className="size-5" />,
    },
    {
      key: 'plugins',
      title: t('Global.Plugins'),
      description: t('Home.PluginsDescription'),
      label: t('Home.CheckOurPlugins'),
      link: getFlotiqMainPageUrl('plugins'),
      icon: <GridIcon className="size-5" />,
    },
    {
      key: 'discord',
      title: 'Discord',
      description: t('Home.DiscordDescription'),
      label: t('Home.GoToDiscord'),
      link: getEnv('VITE_DISCORD') || '',
      icon: <DiscordMarkIcon className="h-4 w-6" />,
    },
  ];

  return (
    <nav
      id="dashboard-section-quick-links"
      aria-labelledby="dashboard-quick-links-heading"
      {...getTestProps(testId, 'quick-links')}
    >
      <h2
        id="dashboard-quick-links-heading"
        className="text-xl mb-2 dark:text-slate-200"
      >
        {t('Home.QuickLinks')}
      </h2>

      <ul className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4 lg:gap-6">
        {links.map((link) => (
          <li key={link.key} className="h-full">
            <a
              className={
                'flex md:flex-col h-full rounded-lg border hover:border-blue p-4 lg:pb-7 gap-3 ' +
                'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 ' +
                'group transition-colors focus:outline-none focus-visible:border-blue dark:text-slate-200'
              }
              href={link.link}
              target="_blank"
              rel="noreferrer"
              {...getTestProps(testId, `${link.key}-quick-link`)}
            >
              <div
                className={
                  'shrink-0 flex size-12 items-center justify-center rounded-md ' +
                  'bg-blue-50 dark:bg-blue text-blue dark:text-slate-200'
                }
                aria-hidden="true"
              >
                {link.icon}
              </div>
              <div className="h-full flex flex-col md:gap-3">
                <h3 className="text-lg font-bold">{link.title}</h3>
                <p className="text-lg">{link.description}</p>
                <span className="pt-1 md:pt-0 block mt-auto text-blue dark:text-slate-200 group-hover:underline">
                  {link.label}
                  <CaretRightIcon className="inline size-2.5 ml-2" />
                </span>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default DashboardQuickLinks;
